import { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import QuoteSection from '../components/QuoteSection';
import LightboxModal from '../components/LightboxModal';
import { companyInfo } from '../data/siteData';
import { imageUrl } from '../lib/images';

export default function PortePage() {
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const galleryImages = [
    { src: "/_assets/dobbeltport-01.webp", title: "Galvaniseret dobbeltport ved erhverv" },
    { src: "/_assets/dobbeltport-03.webp", title: "Mørkegrå portkonstruktion med geometriske felter" },
    { src: "/_assets/foldeport.webp", title: "Bred foldeport i træ og stål" },
    { src: "/_assets/pxl-20260705-162325452.jpg", title: "Galvaniseret svingport i smedejern med automatik" },
    { src: "/_assets/thumbnail-img20250328120707.jpg", title: "Skydeport monteret i indkørsel" },
    { src: "/_assets/thumbnail-img20250417120146.jpg", title: "Foldeport i funktionelt design" },
  ];
  const gateTypes = [
    {
      title: "Dobbeltporte",
      href: "/dobbeltporte",
      image: "/_assets/dobbeltport-03.webp",
      badge: "Klassisk elegance",
      desc: "Symmetriske to-fløjede porte i galvaniseret stål eller mørkegrå profiler til private og erhverv.",
      bullets: [
        "Maksimal passagebredde",
        "Eksklusivt symmetrisk udtryk",
        "Udføres på specialmål",
        "Galvaniseret eller lakeret finish"
      ]
    },
    {
      title: "Skydeporte",
      href: "/skydeporte",
      image: "/_assets/thumbnail-img20250328120707.jpg",
      badge: "Mest populære port",
      desc: "Pladsbesparende, elegante og nemme at betjene – manuelt eller fuldautomatisk til privat og erhverv.",
      bullets: [
        "Fleksibilitet i design og mål",
        "Lang holdbarhed og minimal vedligeholdelse",
        "Manuel eller automatisk åbning",
        "Øget sikkerhed og ejendomsværdi"
      ]
    },
    {
      title: "Foldeporte",
      href: "/foldeporte-i-funktionelt-design",
      image: "/_assets/foldeport.webp",
      badge: "Pladsbesparende",
      desc: "Fleksible, funktionelle og ideelle til både private og erhverv med begrænset plads.",
      bullets: [
        "Pladsbesparende konstruktion",
        "Hurtig og nem adgang til indkørsel",
        "Tilpasses i både mål og design",
        "Robust klejnsmedekvalitet"
      ]
    }
  ];

  return (
    <>
      <SEO
        title="Porte – sikkerhed og design i samme løsning | Hovendals"
        description="Porte i høj kvalitet Hos Hovendal tilbyder vi både skyde- og foldeporte, der kombinerer funktionalitet, sikkerhed og design. Uanset om det er til privat eller erhverv, leverer vi holdbare løsninger, der giver et flot udtryk og nem adgang til din ejendom. Vi tilbyder blandt andet:"
        ogImage="/_assets/485170711-1308137817341082-5036954073090041011-n-6.jpg"
      />

      {/* Hero */}
      <section className="relative py-20 bg-slate-900 text-white overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-25">
          <img
            src={imageUrl("/_assets/485170711-1308137817341082-5036954073090041011-n-6.jpg", "width=1600,quality=80,format=auto")}
            alt="Porte"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-[#004fa4]/20 text-[#c2ccdf] border border-[#004fa4]/30">
              <i className="fa-sharp fa-solid fa-warehouse" aria-hidden="true"></i>
              <span>Sikkerhed & design</span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white leading-tight">
              Porte – sikkerhed og design i samme løsning
            </h1>
            <p className="text-base sm:text-lg text-slate-300 leading-relaxed font-normal">
              En port er ikke kun en praktisk adgangsvej – den er også en vigtig del af helhedsindtrykket af din ejendom. Hos Hovendals leverer vi porte, der kombinerer styrke, funktionalitet og æstetik, så du får en løsning, der både passer til dit hegn og opfylder dine daglige behov.
            </p>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-3">
            <span className="text-xs font-bold uppercase tracking-wider text-[#004fa4]">
              Kvalitetsporte
            </span>
            <h2 className="text-2xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
              Porte i høj kvalitet
            </h2>
            <p className="text-slate-700 leading-relaxed text-base">
              Hos Hovendal tilbyder vi både skyde- og foldeporte, der kombinerer funktionalitet, sikkerhed og design. Uanset om det er til privat eller erhverv, leverer vi holdbare løsninger, der giver et flot udtryk og nem adgang til din ejendom.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {gateTypes.map((gate) => (
              <div
                key={gate.title}
                className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col justify-between group"
              >
                <div>
                  <div className="relative h-64 overflow-hidden bg-slate-100">
                    <img
                      src={imageUrl(gate.image, "width=800,quality=85,format=auto")}
                      alt={gate.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute top-4 left-4 bg-slate-950/80 backdrop-blur-sm text-white text-xs font-bold px-3 py-1 rounded-full">
                      {gate.badge}
                    </div>
                  </div>
                  <div className="p-6 sm:p-8 space-y-4">
                    <h3 className="text-2xl font-bold text-slate-900 group-hover:text-[#004fa4] transition-colors">
                      {gate.title}
                    </h3>
                    <p className="text-slate-600 text-sm leading-relaxed">
                      {gate.desc}
                    </p>
                    <ul className="space-y-2 pt-2 border-t border-slate-100 text-xs text-slate-700 font-medium">
                      {gate.bullets.map((b, i) => (
                        <li key={i} className="flex items-center gap-2">
                          <i className="fa-sharp fa-solid fa-check text-[#004fa4]" aria-hidden="true"></i>
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="p-6 sm:p-8 pt-0">
                  <Link
                    to={gate.href}
                    className="w-full py-3 rounded-lg bg-[#004FA4] hover:bg-[#183E7F] text-white font-medium text-sm text-center transition-colors flex items-center justify-center gap-2"
                  >
                    <span>Læs mere om {gate.title.toLowerCase()}</span>
                    <i className="fa-sharp fa-solid fa-arrow-right text-xs" aria-hidden="true"></i>
                  </Link>
                </div>
              </div>
            ))}
          </div>

          {/* Galleri over portløsninger */}
          <div className="mb-16">
            <div className="text-center max-w-2xl mx-auto mb-8">
              <span className="text-xs font-bold uppercase tracking-wider text-[#004fa4]">Referencer</span>
              <h3 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight mt-1">
                Udvalgte portløsninger
              </h3>
              <p className="text-sm text-slate-600 mt-2">
                Se eksempler på automatiske svingporte, skydeporte og foldeporte, vi har monteret for kunder.
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {galleryImages.map((img, idx) => (
                <div
                  key={idx}
                  onClick={() => setLightboxIndex(idx)}
                  className="group relative h-40 sm:h-48 rounded-xl overflow-hidden bg-slate-100 cursor-pointer shadow-sm hover:shadow-md transition-all"
                >
                  <img
                    src={imageUrl(img.src, "width=400,quality=80,format=auto")}
                    alt={img.title}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-slate-950/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white">
                    <span className="p-2 rounded-full bg-[#143463]/90 text-white text-xs">
                      <i className="fa-sharp fa-solid fa-magnifying-glass-plus" aria-hidden="true"></i>
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-slate-50 rounded-2xl p-8 border border-slate-200 text-center max-w-3xl mx-auto space-y-3">
            <p className="text-slate-800 font-semibold text-base">
              Med en port fra Hovendals får du en holdbar og stilren løsning, der giver tryghed, funktionalitet og et flot udtryk.
            </p>
            <div className="flex justify-center gap-4 pt-2">
              <a
                href={`tel:${companyInfo.phoneFormatted}`}
                className="px-6 py-2.5 rounded-lg bg-[#004fa4] hover:bg-[#183e7f] text-white font-medium text-sm shadow-sm transition-all"
              >
                Ring {companyInfo.phone}
              </a>
              <Link
                to="/kontakt"
                className="px-6 py-2.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white font-bold text-sm transition-all"
              >
                Få et tilbud
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <QuoteSection />

      {/* Lightbox Modal */}
      <LightboxModal
        isOpen={lightboxIndex !== null}
        onClose={() => setLightboxIndex(null)}
        images={galleryImages.map(img => ({ src: img.src, title: img.title }))}
        currentIndex={lightboxIndex || 0}
        onNavigate={(newIdx) => setLightboxIndex(newIdx)}
      />
    </>
  );
}
